import SeoHead from "../../components/Seo/SeoHead";
import WhatsAppMockup from "../../components/Marketing/WhatsAppMockup";
import FaqAccordion from "../../components/Marketing/FaqAccordion";
import { Link } from "react-router-dom";
import {
  TbTools,
  TbCar,
  TbMessageCircle,
  TbDeviceMobile,
  TbCircleCheck,
} from "react-icons/tb";
import styles from "./Mecanicas.module.css";
import {
  BUSINESS_SEGMENTS,
  CTA_PAGES,
  CTA_PLACEMENTS,
  DESTINATIONS,
  trackTrialCtaClick,
} from "../../analytics";
import {
  TRIAL_PRODUCTS,
  buildTrialSignupPath,
} from "../../constants/trialProduct";

const MECANICAS_FAQ = [
  {
    question: "O bot dá orçamento fechado ou estimado?",
    answer:
      "Estimado. O chatbot informa o preço médio dos serviços padrão com base na tabela que você cadastra. O orçamento definitivo é confirmado pelo mecânico após avaliar o veículo.",
  },
  {
    question: "O cliente pode enviar foto do problema pelo WhatsApp?",
    answer:
      "A foto chega no seu WhatsApp normalmente e você vê. O bot, porém, entende texto e áudio — se vier uma imagem, ele avisa o cliente que não consegue abrir e pede que descreva o problema, para não fingir que analisou.",
  },
  {
    question: "Funciona para motos e caminhões também?",
    answer:
      "Sim. Antes de marcar, o bot pergunta modelo, ano e quilometragem, e registra junto do agendamento — o mecânico recebe o pedido já com esses dados, seja carro, moto ou caminhão.",
  },
  {
    question: "Como o cliente acompanha o status do serviço?",
    answer:
      "Quando você marca o serviço como pronto no painel, o bot avisa o cliente no WhatsApp que o veículo está liberado para retirada — que é a mensagem que evita a ligação de \"já ficou?\". Os estágios intermediários você acompanha no painel.",
  },
  {
    question: "E os lembretes de revisão periódica?",
    answer:
      "O sistema registra a data do último serviço e avisa o cliente quando está na hora da próxima revisão — trazendo o cliente de volta antes que ele vá para a concorrência.",
  },
  {
    question: "Posso cadastrar minha tabela de preços de serviços?",
    answer:
      "Sim. Você cadastra os serviços com preços médios e o bot usa essa tabela para responder estimativas automaticamente.",
  },
];

const MECANICAS_MOCKUP = [
  { type: "out", text: "Bom dia, meu carro tá fazendo barulho na frente" },
  {
    type: "in",
    text: "Bom dia! Pra agilizar, qual a marca e modelo do carro?",
  },
  { type: "out", text: "Civic 2018" },
  {
    type: "in",
    text: "Anotado. Quando você consegue trazer pra olharmos?",
    buttons: ["Hoje à tarde", "Amanhã cedo", "Sexta"],
  },
  { type: "out", text: "Amanhã cedo" },
  {
    type: "in",
    text: "✅ Te encaixei amanhã 8h. O mecânico vai te chamar aqui depois de avaliar.",
  },
];

const SERVICE_LD = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Chatbot de WhatsApp para Mecânica",
  provider: { "@type": "Organization", name: "Nexus Chatbot" },
  areaServed: "BR",
  serviceType: "Chatbot de WhatsApp para Oficina Mecânica",
};

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: MECANICAS_FAQ.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const DORES = [
  {
    Icon: TbTools,
    title: "Mão suja, telefone tocando",
    text: "Com graxa nas mãos, o mecânico não atende. O cliente liga, cai na caixa postal, vai para a concorrência.",
  },
  {
    Icon: TbCar,
    title: "Cliente sem hora marcada",
    text: "Chega na oficina cheia, espera ou vai embora irritado. Agendamento evita aglomeração e perda.",
  },
  {
    Icon: TbMessageCircle,
    title: '"Troca de óleo quanto sai?"',
    text: "A mesma pergunta dezenas de vezes por dia. O bot responde com estimativa automática 24/7.",
  },
  {
    Icon: TbDeviceMobile,
    title: '"Tá pronto?"',
    text: "Clientes ligando para saber o status. O bot envia atualizações automáticas sem você precisar parar.",
  },
];

const RECURSOS = [
  "Agendamento com seleção de serviço (troca de óleo, revisão, diagnóstico)",
  "Orçamento estimado para serviços padrão (tabela configurável)",
  "Coleta prévia: modelo, ano, km e sintoma do veículo",
  "Status do serviço em tempo real via WhatsApp",
  "Lembrete de revisão periódica para trazer o cliente de volta",
  "Registro de fotos de diagnóstico enviadas pelo cliente",
];

/** Contexto fixo desta página para o funil de teste grátis (GA4). */
const CTA_CTX = {
  cta_page: CTA_PAGES.MECANICAS,
  business_segment: BUSINESS_SEGMENTS.MECANICAS,
  destination: DESTINATIONS.LOGIN_SIGNUP,
  // Página de segmento vende AGENDA (o cliente marca hora): o teste que ela
  // oferece é o de Agendamento, e o `?produto=` leva isso ao signup.
  product: TRIAL_PRODUCTS.AGENDAMENTO,
};

/** Cadastro já carregando o produto — o backend escolhe o plano de trial. */
const SIGNUP_PATH = buildTrialSignupPath(TRIAL_PRODUCTS.AGENDAMENTO);

const Mecanicas = () => (
  <>
    <SeoHead
      title="Chatbot WhatsApp para Mecânica — Orçamentos e Agendamentos | Nexus Chatbot"
      description="Receba pedidos de orçamento, agende revisões e notifique o status do serviço pelo WhatsApp automaticamente. Teste grátis 14 dias."
      canonical="https://nexustech.tec.br/mecanicas"
      jsonLd={[SERVICE_LD, FAQ_LD]}
    />

    {/* Hero */}
    <section className={styles.hero}>
      <div className={styles.heroInner}>
        <div className={styles.heroCol}>
          <h1 className={styles.heroTitle}>
            <TbTools
              className={styles.titleIcon}
              size={38}
              aria-hidden="true"
            />
            Chatbot de WhatsApp para Mecânica que Responde Orçamentos e Agenda
            Revisões
          </h1>
          <p className={styles.heroText}>
            Enquanto você trabalha no carro, o bot atende o próximo cliente —
            responde orçamentos, agenda horários e avisa quando o serviço tiver
            pronto.
          </p>
          <Link
            to={SIGNUP_PATH}
            className={styles.ctaPrimary}
            onClick={() =>
              trackTrialCtaClick({
                ...CTA_CTX,
                cta_placement: CTA_PLACEMENTS.HERO,
              })
            }
          >
            Testar grátis 14 dias
          </Link>
          <p className={styles.heroNote}>
            Sem cartão. Cancele quando quiser. Configuração em 15 minutos.
          </p>
        </div>
        <div className={styles.heroMockup}>
          <WhatsAppMockup messages={MECANICAS_MOCKUP} looping />
        </div>
      </div>
    </section>

    {/* Dores */}
    <section className={styles.dores}>
      <div className={styles.doresInner}>
        <h2 className={styles.sectionTitle}>
          Por que mecânicas usam a Nexus Tech
        </h2>
        <div className={styles.doresGrid}>
          {DORES.map((d, i) => (
            <div key={i} className={styles.doreCard}>
              <d.Icon
                className={styles.doreIcon}
                size={36}
                aria-hidden="true"
              />
              <h3 className={styles.doreTitle}>{d.title}</h3>
              <p className={styles.doreText}>{d.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA intermediário */}
    <section className={styles.ctaMid}>
      <div className={styles.ctaMidInner}>
        <p className={styles.ctaMidText}>
          Pare de perder orçamento no caixa postal.
        </p>
        <Link
          to={SIGNUP_PATH}
          className={styles.ctaPrimary}
          onClick={() =>
            trackTrialCtaClick({
              ...CTA_CTX,
              cta_placement: CTA_PLACEMENTS.MIDDLE,
            })
          }
        >
          Testar grátis 14 dias
        </Link>
      </div>
    </section>

    {/* Recursos */}
    <section className={styles.recursos}>
      <div className={styles.recursosInner}>
        <h2 className={styles.sectionTitle}>
          Recursos pensados para mecânicas
        </h2>
        <div className={styles.recursosGrid}>
          {RECURSOS.map((r, i) => (
            <div key={i} className={styles.recursoCard}>
              <TbCircleCheck
                className={styles.recursoIcon}
                size={20}
                aria-hidden="true"
              />
              <p className={styles.recursoText}>{r}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* O que o bot NÃO faz */}
    <section className={styles.naoFaz}>
      <div className={styles.naoFazInner}>
        <h3 className={styles.naoFazTitle}>
          O que o bot NÃO faz — e por que isso é bom
        </h3>
        <p className={styles.naoFazText}>
          Diagnóstico, orçamento definitivo e negociação continuam com você. O
          bot é seu recepcionista digital: coleta as informações, agenda os
          serviços previsíveis e organiza a fila de entrada. Quando precisa de
          mecânico de verdade, ele te chama.
        </p>
      </div>
    </section>

    {/* FAQ */}
    <section className={styles.faq}>
      <div className={styles.faqInner}>
        <h2 className={styles.faqTitle}>Perguntas frequentes</h2>
        <FaqAccordion items={MECANICAS_FAQ} />
      </div>
    </section>

    {/* CTA Final */}
    <section className={styles.ctaFinal}>
      <h2 className={styles.ctaFinalTitle}>
        Menos telefone. Mais carros atendidos.
      </h2>
      <p className={styles.ctaFinalText}>
        Configure em 15 minutos e comece a receber orçamentos automáticos hoje.
      </p>
      <Link
        to={SIGNUP_PATH}
        className={styles.ctaInverse}
        onClick={() =>
          trackTrialCtaClick({
            ...CTA_CTX,
            cta_placement: CTA_PLACEMENTS.FINAL,
          })
        }
      >
        Testar grátis 14 dias
        <TbTools size={18} aria-hidden="true" />
      </Link>
    </section>
  </>
);

export default Mecanicas;
